import { useContext, useState } from "react";
import {
  InputGroup,
  InputGroupAddon,
  Button,
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
} from "reactstrap";
import { useDebouncedCallback } from "use-debounce";
import { getLocation, getAutoCompleteLocation } from "../services/apiService";
import { ToolContext } from "../context/ToolContext";
import { useAuth } from "../context/AuthContext";
import useUserInfoAndLogging from "../utils/useUserInfoAndLogging";
import { buildAlertForError } from "../utils/alertFunctions";
import { PromptInput } from "./PromptInput";
import { miscEvent } from "../model/event/vndEvent";
import { logEvent } from "../services/apiService";
import "./MapSearch.css";

interface MapSearchProps {
  autoComplete: boolean;
}

const MapSearch = ({ autoComplete }: MapSearchProps) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const { toolState, setToolState } = useContext(ToolContext);
  const { logException } = useUserInfoAndLogging();
  const [autoCompleteLocation, setAutoCompleteLocation] = useState<any[] | null>(null);
  const { getToken } = useAuth();

  const postcodeToSetView = async () => {
    const token = await getToken();
    const location = await getLocation(token, searchValue);
    await logEvent(await getToken(), new miscEvent("MapSearchStandard"));

    if (location instanceof Error) {
      logException(location);
      setToolState({
        alert: buildAlertForError(location, "Error finding location from server"),
      });
      return;
    }

    if (location?.data?.messages?.length > 0) {
      setToolState({
        ...toolState,
        alert: {
          type: "warning",
          className: "warning",
          messages: location?.data?.messages,
        },
      });
      return;
    }

    if (!location.data || location.data.totalResults === 0) {
      return;
    }

    const x = location.data.locationValue_X;
    const y = location.data.locationValue_Y;
    const latLng = { lat: x, lng: y };

    setToolState({
      navigateToLatLng: latLng,
    });
    const element: any = document.getElementById("location-search");
    element.value = searchValue;
  };

  const handleLocationChange = useDebouncedCallback(async (value) => {
    if (autoComplete) {
      if (value.length < 3) {
        setAutoCompleteLocation(null);
        return;
      }
      const token = await getToken();
      const location = await getAutoCompleteLocation(token, value);
      if (location instanceof Error) {
        logException(location);
      } else {
        setAutoCompleteLocation(location.data.results);
      }
    }
    setSearchValue(value);
  }, 300);

  const autoCompleteLocationToSetView = async (location: any) => {
    const latLng = { lat: location.lat, lng: location.lng };
    setToolState({
      navigateToLatLng: latLng,
    });
    setAutoCompleteLocation(null);
    const element: any = document.getElementById("location-search");
    element.value = location.place;
    await logEvent(await getToken(), new miscEvent("MapSearchAutoComplete"));
  };

  return (
    <div className="m-3 position-relative">
      <InputGroup>
        <PromptInput
          autoComplete={autoComplete ? "off" : "on"}
          type="text"
          id="location-search"
          placeholder="Search for a Location"
          bsSize="sm"
          onChange={(e) => handleLocationChange(e.target.value)}
        />
        {!autoComplete && (
          <InputGroupAddon addonType="append">
            <Button
              id="btnSearchSub"
              size="sm"
              color="primary"
              disabled={!searchValue}
              onClick={() => postcodeToSetView()}
            >
              Search
            </Button>
          </InputGroupAddon>
        )}
      </InputGroup>
      {autoComplete && Array.isArray(autoCompleteLocation) && (
        <ListGroup className="autocomplete w-100 mt-2">
          <>
            {autoCompleteLocation.length > 0 ? (
              autoCompleteLocation.map((location: any) => (
                <ListGroupItem
                  key={location.placeId}
                  action
                  tag="button"
                  className="text-light bg-dark p-2"
                  onClick={() => autoCompleteLocationToSetView(location)}
                >
                  <ListGroupItemHeading className="font-bold m-0" tag="h6">
                    <strong>{location.place}</strong>
                  </ListGroupItemHeading>
                  <ListGroupItemText className="m-0">{location.address}</ListGroupItemText>
                </ListGroupItem>
              ))
            ) : (
              <ListGroupItem key={"locationEmptyLabel"} action className="text-muted bg-dark p-2">
                <ListGroupItemHeading className="m-0" tag="h6">
                  <span className="small">No matches found</span>
                </ListGroupItemHeading>
              </ListGroupItem>
            )}
          </>
        </ListGroup>
      )}
    </div>
  );
};

export default MapSearch;
